import { STEERFI_PARTY_ID } from '@steerfi/shared';
import { Building2, Plus, Upload, User, Users } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getSupplierAddConfig,
  getSupplierLabels,
  getSuppliers,
  type SupplierAddType,
  type SupplierFilters,
  type SuppliersResponse,
} from '@/api/suppliers';
import type { DataTableResponse } from '@/components/data-table';
import { SearchInput } from '@/components/search-input';
import { SupplierAddModal } from '@/components/supplier-add-modal';
import { SupplierFileUploadModal } from '@/components/supplier-file-upload-modal';
import type { Supplier } from '@/components/suppliers-table';
import {
  DEFAULT_SUPPLIER_SORT_BY,
  readSupplierSortBy,
  SuppliersTable,
} from '@/components/suppliers-table';
import { Button } from '@/components/ui/button';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useKeycloakAuth } from '@/contexts/keycloak-auth-context';
import { createSortMapper, SUPPLIERS_SORT_MAP } from '@/lib/sort-mapping';

type PayeeTypeFilter = 'all' | 'BUSINESS' | 'INDIVIDUAL';

function getPayeeTypeConfig(
  pluralLabel: string
): Record<PayeeTypeFilter, { label: string; pageTitle: string; icon: React.ReactNode }> {
  return {
    all: { label: 'All', pageTitle: pluralLabel, icon: <Users className="h-4 w-4" /> },
    BUSINESS: {
      label: 'Companies',
      pageTitle: pluralLabel,
      icon: <Building2 className="h-4 w-4" />,
    },
    INDIVIDUAL: {
      label: 'Individuals',
      pageTitle: pluralLabel,
      icon: <User className="h-4 w-4" />,
    },
  };
}

const DEFAULT_ALLOWED_TYPES: SupplierAddType[] = ['individual', 'business'];

/** Map add-config allowed type to API payee type filter */
const ADD_TYPE_TO_PAYEE: Record<SupplierAddType, PayeeTypeFilter> = {
  individual: 'INDIVIDUAL',
  business: 'BUSINESS',
};

function getAllOrCsv(searchParams: URLSearchParams, key: string): string[] {
  const repeated = searchParams
    .getAll(key)
    .map((s) => s.trim())
    .filter(Boolean);
  if (repeated.length > 0) return repeated;
  const single = searchParams.get(key);
  if (!single) return [];
  return single
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function buildSuppliersSearchParams(filters: SupplierFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.page && filters.page !== 1) params.set('page', String(filters.page));
  if (filters.limit && filters.limit !== 10) params.set('limit', String(filters.limit));
  if (filters.sortBy && filters.sortBy !== DEFAULT_SUPPLIER_SORT_BY)
    params.set('sortBy', filters.sortBy);
  if (filters.order && filters.order !== 'desc') params.set('sortOrder', filters.order);

  const status = filters.status;
  const statusArr = Array.isArray(status) ? status : status ? [status] : [];
  statusArr.filter(Boolean).forEach((v) => params.append('status', v));

  const type = filters.type;
  const typeArr = Array.isArray(type) ? type : type ? [type] : [];
  typeArr.filter(Boolean).forEach((v) => params.append('type', v));

  if (filters.clientId) params.set('clientId', filters.clientId);

  const approval = filters.relationshipApproval;
  const approvalArr = Array.isArray(approval) ? approval : approval ? [approval] : [];
  approvalArr.filter(Boolean).forEach((v) => params.append('relationshipApproval', v));

  const method = filters.preferredPaymentMethod;
  const methodArr = Array.isArray(method) ? method : method ? [method] : [];
  methodArr.filter(Boolean).forEach((v) => params.append('preferredPaymentMethod', v));

  if (filters.search) params.set('search', filters.search);
  if (filters.createdByFilter && filters.createdByFilter !== 'all') {
    params.set('createdByFilter', filters.createdByFilter);
  }

  return params;
}

const sortMapper = createSortMapper(SUPPLIERS_SORT_MAP);

export const Suppliers: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading, user } = useKeycloakAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const lastWrittenQueryRef = useRef<string | null>(null);
  const [suppliersData, setSuppliersData] = useState<SuppliersResponse | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [addSupplierModalOpen, setAddSupplierModalOpen] = useState(false);
  const hasLoadedRef = useRef(false);
  const [allowedTypes, setAllowedTypes] = useState<SupplierAddType[]>(DEFAULT_ALLOWED_TYPES);
  const [displayName, setDisplayName] = useState<string>('supplier');
  const [supportedUploadTypes, setSupportedUploadTypes] = useState<{
    batch: boolean;
    individual: boolean;
  }>({ batch: true, individual: true });
  const [configLoaded, setConfigLoaded] = useState(false);
  const [externalRefLabel, setExternalRefLabel] = useState<string>('External Ref');
  const initialClientId = searchParams.get('clientId')?.trim() || undefined;
  const initialType = getAllOrCsv(searchParams, 'type');
  const initialPayeeType: PayeeTypeFilter =
    initialType.length === 1 && (initialType[0] === 'BUSINESS' || initialType[0] === 'INDIVIDUAL')
      ? (initialType[0] as PayeeTypeFilter)
      : 'all';
  const initialSearch = searchParams.get('search')?.trim() || '';
  const initialStatus = getAllOrCsv(searchParams, 'status');
  const initialRelationshipApprovalArr = getAllOrCsv(searchParams, 'relationshipApproval');
  const initialPreferredPaymentMethodArr = getAllOrCsv(searchParams, 'preferredPaymentMethod');

  const [payeeType, setPayeeType] = useState<PayeeTypeFilter>(initialPayeeType);
  const [filters, setFilters] = useState<SupplierFilters>(() => ({
    page: Number(searchParams.get('page') || 1) || 1,
    limit: Number(searchParams.get('limit') || 10) || 10,
    sortBy: readSupplierSortBy(searchParams.get('sortBy'), DEFAULT_SUPPLIER_SORT_BY),
    order: ((searchParams.get('sortOrder') || 'desc').toLowerCase() as 'asc' | 'desc') || 'desc',
    createdByFilter: (searchParams.get('createdByFilter') as 'all' | 'me') || 'all',
    ...(initialStatus.length > 0 ? { status: initialStatus } : {}),
    ...(initialType.length > 0 && initialType.length <= 2 ? { type: initialType } : {}),
    ...(initialClientId ? { clientId: initialClientId } : {}),
    ...(initialRelationshipApprovalArr.length > 0
      ? { relationshipApproval: initialRelationshipApprovalArr }
      : {}),
    ...(initialPreferredPaymentMethodArr.length > 0
      ? { preferredPaymentMethod: initialPreferredPaymentMethodArr }
      : {}),
    ...(initialSearch ? { search: initialSearch } : {}),
  }));
  const [search, setSearch] = useState(initialSearch);

  const fetchSuppliers = useCallback(
    async (currentFilters: SupplierFilters) => {
      const isInitialLoad = !hasLoadedRef.current;
      try {
        if (isInitialLoad) {
          setIsLoading(true);
        }
        setError(null);
        const data = await getSuppliers(currentFilters);
        setSuppliersData(data);
        hasLoadedRef.current = true;
      } catch (err) {
        const labels = getSupplierLabels(displayName);
        setError(
          err instanceof Error ? err.message : `Failed to load ${labels.plural.toLowerCase()}`
        );
      } finally {
        if (isInitialLoad) {
          setIsLoading(false);
        }
      }
    },
    [displayName]
  );

  // Handle filter changes from the DataTable
  const handleFilterChange = useCallback((filterKey: string, value: string | string[]) => {
    setFilters((prev) => {
      const next: SupplierFilters = { ...prev, page: 1 };
      const shouldClear = Array.isArray(value) ? value.length === 0 : value.trim() === '';

      if (filterKey === 'createdByFilter') {
        next.createdByFilter = shouldClear ? 'all' : (value as 'all' | 'me');
        return next;
      }

      if (shouldClear) {
        delete (next as Record<string, unknown>)[filterKey];
        return next;
      }

      (next as Record<string, unknown>)[filterKey] = value;
      return next;
    });
  }, []);

  // Handle sort changes from the DataTable
  const handleSortChange = useCallback((sortBy: string, order: 'asc' | 'desc') => {
    const backendSortBy = sortMapper.toBackend(sortBy);
    if (!backendSortBy) return;
    setFilters((prev) => ({
      ...prev,
      sortBy: backendSortBy,
      order,
    }));
  }, []);

  // Handle page changes from the DataTable
  const handlePageChange = useCallback((page: number) => {
    setFilters((prev) => ({
      ...prev,
      page,
    }));
  }, []);

  // Handle payee type filter changes
  const handlePayeeTypeChange = useCallback((value: string) => {
    if (value) {
      const newType = value as PayeeTypeFilter;
      setPayeeType(newType);
      setFilters((prev) => {
        const { type: _prevType, ...rest } = prev;
        if (newType === 'all') {
          return { ...rest, page: 1 };
        }
        return { ...rest, page: 1, type: newType };
      });
    }
  }, []);

  // Sync URL -> state (supports back/forward and shared links)
  useEffect(() => {
    const current = searchParams.toString();
    if (lastWrittenQueryRef.current && lastWrittenQueryRef.current === current) {
      lastWrittenQueryRef.current = null;
      return;
    }

    const nextStatus = getAllOrCsv(searchParams, 'status');
    const nextType = getAllOrCsv(searchParams, 'type');
    const nextPayeeType: PayeeTypeFilter =
      nextType.length === 1 && (nextType[0] === 'BUSINESS' || nextType[0] === 'INDIVIDUAL')
        ? (nextType[0] as PayeeTypeFilter)
        : 'all';
    const nextSearch = searchParams.get('search')?.trim() || '';
    const nextClientId = searchParams.get('clientId')?.trim() || '';
    const nextRelationshipApprovalArr = getAllOrCsv(searchParams, 'relationshipApproval');
    const nextPreferredPaymentMethodArr = getAllOrCsv(searchParams, 'preferredPaymentMethod');

    setPayeeType(nextPayeeType);
    setSearch(nextSearch);
    setFilters((prev) => {
      const next: SupplierFilters = {
        ...prev,
        page: Number(searchParams.get('page') || 1) || 1,
        limit: Number(searchParams.get('limit') || prev.limit || 10) || prev.limit || 10,
        sortBy: readSupplierSortBy(
          searchParams.get('sortBy'),
          prev.sortBy ?? DEFAULT_SUPPLIER_SORT_BY
        ),
        order:
          ((searchParams.get('sortOrder') || prev.order || 'desc').toLowerCase() as
            | 'asc'
            | 'desc') || 'desc',
        createdByFilter: (searchParams.get('createdByFilter') as 'all' | 'me') || 'all',
      };

      if (nextStatus.length > 0) {
        next.status = nextStatus;
      } else {
        delete next.status;
      }

      if (nextType.length > 0) {
        next.type = nextType;
      } else {
        delete next.type;
      }

      if (nextClientId) {
        next.clientId = nextClientId;
      } else {
        delete next.clientId;
      }

      if (nextRelationshipApprovalArr.length > 0) {
        next.relationshipApproval = nextRelationshipApprovalArr;
      } else {
        delete next.relationshipApproval;
      }

      if (nextPreferredPaymentMethodArr.length > 0) {
        next.preferredPaymentMethod = nextPreferredPaymentMethodArr;
      } else {
        delete next.preferredPaymentMethod;
      }

      if (nextSearch) {
        next.search = nextSearch;
      } else {
        delete next.search;
      }

      return next;
    });
  }, [searchParams]);

  // Sync state -> URL (so refresh/share preserves the current view)
  useEffect(() => {
    const nextParams = buildSuppliersSearchParams(filters);
    const next = nextParams.toString();
    const current = searchParams.toString();
    if (next === current) return;
    lastWrittenQueryRef.current = next;
    setSearchParams(nextParams, { replace: true });
  }, [filters, searchParams, setSearchParams]);

  // Fetch add-config once (display name, upload types, allowed add types)
  const partyId = user?.partyId || STEERFI_PARTY_ID;
  useEffect(() => {
    if (!isAuthenticated || !user?.partyId) {
      setConfigLoaded(true);
      return;
    }
    setConfigLoaded(false);
    getSupplierAddConfig(partyId)
      .then((config) => {
        const types = config.allowedTypes?.length ? config.allowedTypes : DEFAULT_ALLOWED_TYPES;
        setAllowedTypes(types);
        setDisplayName(config.displayName ?? 'supplier');
        setSupportedUploadTypes(config.supportedUploadTypes ?? { batch: true, individual: true });
        setExternalRefLabel(config.externalRefDisplayName?.trim() || 'External Ref');
        if (types.length === 1) {
          const singlePayee = ADD_TYPE_TO_PAYEE[types[0]];
          setPayeeType(singlePayee);
          setFilters((prev) => ({ ...prev, page: 1, type: singlePayee }));
        }
      })
      .catch(() => {
        setAllowedTypes(DEFAULT_ALLOWED_TYPES);
        setDisplayName('supplier');
        setSupportedUploadTypes({ batch: true, individual: true });
      })
      .finally(() => setConfigLoaded(true));
  }, [isAuthenticated, user?.partyId, partyId]);

  useEffect(() => {
    // Only fetch suppliers if user is authenticated and not loading
    if (isAuthenticated && !authLoading) {
      fetchSuppliers(filters);
    } else if (!authLoading && !isAuthenticated) {
      setError(`Please log in to view ${getSupplierLabels(displayName).plural.toLowerCase()}`);
      setIsLoading(false);
    }
  }, [filters, fetchSuppliers, isAuthenticated, authLoading, displayName]);

  useEffect(() => {
    const handler = () => {
      fetchSuppliers(filters);
    };
    window.addEventListener('steerfi:supplier-notes-changed', handler);
    return () => window.removeEventListener('steerfi:supplier-notes-changed', handler);
  }, [fetchSuppliers, filters]);

  const getSupplierLink = (supplier: Supplier) => ({ to: `/suppliers/${supplier.id}` });

  // Transform the SuppliersResponse to match DataTableResponse format
  const tableData: DataTableResponse<Supplier> | undefined = suppliersData
    ? {
        data: suppliersData.data.map((supplier) => ({
          id: supplier.id,
          name: supplier.business_name || `${supplier.first_name} ${supplier.last_name}`.trim(),
          clientName: supplier.client_name,
          preferredPaymentMethod: supplier.preferred_payment_method,
          email: supplier.email || 'No email',
          verificationStatus: supplier.verification_status ?? '—',
          relationshipApproval: supplier.relationship_approval ?? null,
          relationshipApprovalAt: supplier.relationship_approval_at
            ? supplier.relationship_approval_at.toString()
            : null,
          externalRef: supplier.external_ref ?? null,
          createdByName: supplier.created_by_name ?? null,
          createdAt: supplier.created_at.toString(),
          ...(typeof supplier.notes_count === 'number' && supplier.notes_count > 0
            ? { notes_count: supplier.notes_count }
            : {}),
        })),
        pagination: {
          page: suppliersData.pagination.page,
          pageSize: suppliersData.pagination.limit,
          total: suppliersData.pagination.total,
          totalPages: suppliersData.pagination.totalPages,
        },
      }
    : undefined;

  const labels = getSupplierLabels(displayName);
  const payeeTypeConfig = getPayeeTypeConfig(labels.plural);
  const currentConfig = payeeTypeConfig[payeeType];

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
      <div className="px-4 lg:px-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-bold tracking-tight min-w-32">
              {currentConfig.pageTitle}
            </h1>
            {configLoaded && allowedTypes.length > 1 && (
              <ToggleGroup
                type="single"
                value={payeeType}
                onValueChange={handlePayeeTypeChange}
                variant="outline"
                size="sm"
              >
                <ToggleGroupItem value="all" aria-label="Show all payees" className="px-6 gap-1.5">
                  {payeeTypeConfig.all.icon}
                  <span className="hidden sm:inline">{payeeTypeConfig.all.label}</span>
                </ToggleGroupItem>
                {allowedTypes.includes('business') && (
                  <ToggleGroupItem
                    value="BUSINESS"
                    aria-label="Show companies only"
                    className="px-6 gap-1.5"
                  >
                    {payeeTypeConfig.BUSINESS.icon}
                    <span className="hidden sm:inline">{payeeTypeConfig.BUSINESS.label}</span>
                  </ToggleGroupItem>
                )}
                {allowedTypes.includes('individual') && (
                  <ToggleGroupItem
                    value="INDIVIDUAL"
                    aria-label="Show individuals only"
                    className="px-6 gap-1.5"
                  >
                    {payeeTypeConfig.INDIVIDUAL.icon}
                    <span className="hidden sm:inline">{payeeTypeConfig.INDIVIDUAL.label}</span>
                  </ToggleGroupItem>
                )}
              </ToggleGroup>
            )}
          </div>
          <div className="flex items-center gap-4">
            {configLoaded && supportedUploadTypes.individual && (
              <Button
                onClick={() => setAddSupplierModalOpen(true)}
                disabled={!isAuthenticated || authLoading}
                variant="outline"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add {labels.singular}
              </Button>
            )}
            {configLoaded && supportedUploadTypes.batch && (
              <Button
                onClick={() => setUploadModalOpen(true)}
                disabled={!isAuthenticated || authLoading}
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload {labels.plural}
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="px-4 lg:px-6">
        <SuppliersTable
          data={tableData}
          isLoading={isLoading}
          error={error}
          getRowLink={getSupplierLink}
          onFilterChange={handleFilterChange}
          onSortChange={handleSortChange}
          currentSortKey={sortMapper.toColumn(filters.sortBy) ?? 'createdAt'}
          currentSortOrder={filters.order ?? 'desc'}
          onPageChange={handlePageChange}
          externalRefLabel={externalRefLabel}
          currentFilterValues={{
            status: Array.isArray(filters.status)
              ? filters.status
              : filters.status
                ? [filters.status]
                : [],
            relationshipApproval: Array.isArray(filters.relationshipApproval)
              ? filters.relationshipApproval
              : filters.relationshipApproval
                ? [filters.relationshipApproval]
                : [],
            preferredPaymentMethod: Array.isArray(filters.preferredPaymentMethod)
              ? filters.preferredPaymentMethod
              : filters.preferredPaymentMethod
                ? [filters.preferredPaymentMethod]
                : [],
            createdByFilter: filters.createdByFilter === 'me' ? 'me' : '',
          }}
          searchInput={
            <SearchInput
              id="suppliers-search"
              value={search}
              onChange={(val) => {
                setSearch(val);
                setFilters((prev) => {
                  const next: SupplierFilters = { ...prev, page: 1 };
                  const v = val.trim();
                  if (v === '') {
                    delete next.search;
                    return next;
                  }
                  next.search = v;
                  return next;
                });
              }}
              placeholder={`Search ${labels.plural.toLowerCase()}...`}
              sessionStorageKey="suppliers_recent_searches"
              suggestions={{
                quickExamples: ['SteerBridge', 'Wilson LLC'],
              }}
            />
          }
        />
      </div>

      {/* Supplier File Upload Modal */}
      {user && (
        <SupplierFileUploadModal
          open={uploadModalOpen}
          onClose={() => setUploadModalOpen(false)}
          onUploadSuccess={() => {
            // Refresh suppliers table after successful upload
            fetchSuppliers(filters);
          }}
          // TODO: Update correctly - for now, using hard-coded admin user's partyId from auth logs
          partyId={user.partyId || STEERFI_PARTY_ID}
        />
      )}

      {/* Add Supplier Modal */}
      {user && (
        <SupplierAddModal
          open={addSupplierModalOpen}
          onClose={() => setAddSupplierModalOpen(false)}
          onAddSuccess={() => {
            fetchSuppliers(filters);
          }}
          partyId={partyId}
          allowedTypes={allowedTypes}
        />
      )}
    </div>
  );
};
